import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) loadUser();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      logout();
      return null;
    }
  };

  const login = async (email, password) => {
    try {
      const cleanEmail = (email || '').trim().toLowerCase();
      const response = await authAPI.login({
        email: cleanEmail,
        password
      });

      const { token: newToken, user: userData } = response.data;

      localStorage.setItem('token', newToken);
      setToken(newToken);

      if (userData) setUser(userData);
      else await fetchUserProfile();

      return response.data;
    } catch (err) {
      throw err;
    }
  };

  const loginWithToken = async (authToken) => {
    try {
      console.log('loginWithToken: Storing token...', authToken.substring(0, 20) + '...');
      localStorage.setItem('token', authToken);
      setToken(authToken);
      console.log('loginWithToken: Fetching user profile...');

      const userData = await fetchUserProfile();
      if (!userData) return { success: false, error: 'Failed to fetch user profile' };
      console.log('loginWithToken: User data received:', userData);

      if (!userData) {
        console.error('loginWithToken: No user data received');
        return { success: false, error: 'Failed to fetch user profile' };
      }

      console.log('loginWithToken: Success!');
      return { success: true, user: userData };
    } catch (error) {
      console.error('loginWithToken: Error occurred:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      return { success: false, error: 'Failed to authenticate with token' };
    }
  };

  const register = async (email, username, password) => {
    const cleanEmail = (email || '').trim().toLowerCase();
    const cleanUsername = (username || '').trim();

    const response = await authAPI.register({
      email: cleanEmail,
      username: cleanUsername,
      password,
    });

    // Backend returns: { message, requiresEmailVerification, user }
    const { token: newToken, user: userData, requiresEmailVerification, message } = response.data;

    // If verification is required, DO NOT authenticate.
    if (requiresEmailVerification) {
      // Important: ensure no previous auth is hanging around
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);

      return { success: true, requiresEmailVerification: true, message, user: userData };
    }

    // If you ever decide to auto-login on register, only do it when a token exists
    if (newToken) {
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData || null);
    }

    return { success: true, requiresEmailVerification: false, message, user: userData };
  };



  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const isAuthenticated = !!token && !!user;

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        loading,
        isAuthenticated,
        login,
        loginWithToken,
        register,
        logout,
        fetchUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
